import { Children, PropsWithChildren } from 'react'
import { Grid, Divider, Typography, makeStyles } from '@material-ui/core'
import { useAsyncEntity } from '@backstage/plugin-catalog-react'
import { EntityAboutCard, EntityLinksCard } from '@backstage/plugin-catalog'
import { EntityCatalogGraphCard } from '@backstage/plugin-catalog-graph'
import { EntityTechInsightsOverviewFailedChecksCard } from '@internal/plugin-tech-insights-overview'
import { DatadogServiceCatalogLinksCard } from '@internal/plugin-datadog-service-catalog'
import { EntityWarningContent } from './EntityWarningContent'
import EntityTechnicalDetailsCard from './EntityTechnicalDetailsCard'

function formatBerlinTime(date: Date) {
  return date.toLocaleString('de-DE', { timeZone: 'Europe/Berlin' }) + ' (Berlin)'
}

function EntityFooter() {
  const { entity, loading } = useAsyncEntity()

  const lastProcessedAtIsoString =
    entity?.metadata.annotations?.['backstage.io/last-processed-at']
  const lastProcessedAt = lastProcessedAtIsoString && new Date(lastProcessedAtIsoString)
  const lastProcessedAtBerlinString = lastProcessedAt && formatBerlinTime(lastProcessedAt)

  return (
    <footer style={{ marginTop: '3rem' }}>
      <Divider />
      <Typography
        variant="body2"
        component="div"
        color="textSecondary"
        style={{ paddingTop: '1rem' }}
      >
        Entity last updated at:{' '}
        {loading ? '...' : lastProcessedAtBerlinString ?? '(never)'}
      </Typography>
    </footer>
  )
}

const GRAPH_HEIGHT = 500
const useStyles = makeStyles({
  // We inject styles to the `EntityCatalogGraphCard` because it's layout was
  // totally broken somewhere between Backstage 1.35 and 1.43 for graphs that
  // don't fit the limited vertical space of the card
  graph: {
    height: `${GRAPH_HEIGHT}px`,
    '& > div': {
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%'
    },
    '& > div > div': {
      display: 'flex',
      height: '100%'
    },
    // "Full Screen" button
    '& button': {
      position: 'absolute',
      top: '8px',
      right: '8px',
      height: '48px',
      width: '48px',
      overflow: 'hidden'
    },
    // The graph itself
    '& svg': {
      maxWidth: '100%',
      maxHeight: '100%',
      width: 'auto',
      height: 'auto'
    }
  }
})

export function EntityOverviewContent(props: PropsWithChildren<{}>) {
  const styles = useStyles()
  return (
    <>
      <EntityWarningContent />
      <Grid container>
        <Grid item lg={8} xs={12}>
          <Grid container direction="column">
            <Grid item>
              <EntityCatalogGraphCard height={GRAPH_HEIGHT} className={styles.graph} />
            </Grid>
            <Grid item>
              <Grid container direction="column">
                {Children.map(props.children, (child, idx) => (
                  <Grid key={idx} item>
                    {child}
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item lg={4}>
          <Grid container direction="column">
            <Grid item>
              <EntityAboutCard />
            </Grid>
            <Grid item>
              <EntityTechnicalDetailsCard />
            </Grid>
            <Grid item>
              <EntityTechInsightsOverviewFailedChecksCard />
            </Grid>
            <Grid item>
              <EntityLinksCard />
            </Grid>
            <Grid item>
              <DatadogServiceCatalogLinksCard />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <EntityFooter />
    </>
  )
}
